import {
  type DocumentMeta,
  type DocumentVersion,
  getDocumentById,
} from "@prismicio/editor-fields"
import {
  cache,
  preload,
  revalidateData,
  updateData,
  useRequest,
} from "@prismicio/editor-support/Suspense"
import {
  Document as DocumentT,
  DocumentLegacy,
} from "@prismicio/types-internal/lib/content"
import {
  flattenCustomTypeFields,
  type StaticCustomType,
} from "@prismicio/types-internal/lib/customtypes"

import { env } from "../app/env"
import { useRepositoryMetaTitle } from "../app/routing"
import {
  baseUrl,
  createDocument as doCreateDocument,
  type CreateDocumentParams,
  getDocumentByVersion,
  saveDocument as doSaveDocument,
  type SaveDocumentParams,
} from "../app/services/core"

export type DocumentData = NewDocumentData | ExistingDocumentData

export type NewDocumentData = {
  type: "new"
  groupLangId?: string
  locale: string
  customType: StaticCustomType
  releaseId?: string
  asCopy?: {
    document: DocumentT
    title: string
    tags: string[]
  }
}

export type ExistingDocumentData = {
  type: "existing"
  meta: DocumentMeta
  version: DocumentVersion
  document: DocumentT
  initialDraft?: DocumentT
  customType: StaticCustomType
}

export function useDocumentMeta(id: string) {
  return useRequest(getDocumentById, [
    baseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    id,
  ])
}

export function useDocument(versionId: string, customType: StaticCustomType) {
  const result = useRequest(getDocumentByVersion, [versionId])
  const doc = DocumentLegacy.parse(result, {
    customTypeId: customType.id,
    fields: flattenCustomTypeFields(customType),
  })
  if (!doc) throw Error("Invalid doc returned from DocumentLegarcy Parse")
  return doc
}

export function preloadDocument(versionId: string) {
  preload(getDocumentByVersion, [versionId])
}

export function hasDocument(versionId: string) {
  return cache.has(getDocumentByVersion, [versionId])
}

export function createDocument(params: CreateDocumentParams) {
  return doCreateDocument(params).then((body) => {
    updateData(
      getDocumentById,
      [baseUrl, env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY, body.id],
      body,
    )
    return body
  })
}

export function saveDocument(params: SaveDocumentParams) {
  return doSaveDocument(params).then((body) => {
    updateData(
      getDocumentById,
      [baseUrl, env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY, params.id],
      body,
      {
        // Let routing be the main change driver here. Fixes a glitch with the version redirecting.
        notify: false,
      },
    )

    const newVersion = body.versions.find(
      (version) => version.status === statusAfterSave[params.status],
    )

    if (!newVersion) throw new Error("Could not find new version.")

    return newVersion
  })
}

// When saving from an unclassified, published or archived version, the changes are
// saved to a new unclassified version. When a release version is saved, it overwrites
// the current release version
const statusAfterSave = {
  unclassified: "unclassified",
  published: "unclassified",
  archived: "unclassified",
  release: "release",
} as const

export function revalidateDocumentMeta(metaId: string): Promise<void> {
  return revalidateData(getDocumentById, [
    baseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    metaId,
  ])
}

export function getDocumentTitle(data: DocumentData) {
  if (data.type === "existing") return data.meta.title
  return data.asCopy?.title
}

export function useDocumentMetaTitle(data: DocumentData) {
  const documentTitle = getDocumentTitle(data)
  useRepositoryMetaTitle(documentTitle ?? "")
}

export function getBaseVersion(
  version: DocumentVersion,
  versions: DocumentVersion[],
): DocumentVersion {
  // Till we fully implement conflict resolution, just save the current draft -
  // using any existing draft as a base version.
  if (version.status !== "published") return version

  const firstDraft = versions.find(
    (version) => version.status === "unclassified",
  )

  return firstDraft ?? version
}
